import type { LucideIcon } from 'lucide-react'
import {
  BookOpen,
  Monitor,
  Server,
  Database,
  Bot,
  Mail,
  Layers,
  MessageCircle,
  KeyRound,
  Info,
} from 'lucide-react'

export interface HandbookBlock {
  heading?: string
  paragraphs?: string[]
  bullets?: string[]
  code?: string[]
  table?: { headers: string[]; rows: string[][] }
  numbered?: string[]
}

export interface HandbookSection {
  id: string
  number: number
  icon: LucideIcon
  title: string
  subtitle: string
  color: string
  lead: string
  blocks: HandbookBlock[]
}

export const handbookSections: HandbookSection[] = [
  {
    id: 'overview',
    number: 1,
    icon: BookOpen,
    title: 'What this is & the big picture',
    subtitle: 'Restaurant analogy, where the code lives, and how requests flow end to end',
    color: 'violet',
    lead:
      'You do not need to be a programmer to read this. When you need to explain the system to someone else, start here.',
    blocks: [
      {
        heading: 'Think of it like a restaurant',
        bullets: [
          'The front of house (website / app you click) takes your order.',
          'The kitchen (backend server) cooks it, talks to suppliers, and saves receipts in a filing cabinet (database).',
          'Sometimes the kitchen calls outside help (email, AI, payments, etc.).',
        ],
      },
      {
        heading: 'Where the code lives',
        bullets: [
          'Frontend — the dizee project: React + Vite app.',
          'Backend — the backend-dizee project: Express API, separate from the frontend.',
          'They talk over the internet (or locally when both run on a laptop).',
        ],
      },
      {
        heading: 'The big picture (one sentence each)',
        bullets: [
          'User opens the web app → the frontend loads in the browser.',
          'The frontend sends requests (e.g. log in, save this show) to the backend at a URL like https://your-api-host/...',
          'The backend reads or writes data in MongoDB (MongoDB Atlas).',
          'The backend can call other services: OpenAI, Mailgun, Stripe, Twilio, AWS S3, Google APIs, Spotify, Soundcharts, etc., depending on the feature.',
        ],
      },
    ],
  },
  {
    id: 'frontend',
    number: 2,
    icon: Monitor,
    title: 'Frontend (the website)',
    subtitle: 'React, Vite, TypeScript, Tailwind — how the browser talks to the API',
    color: 'sky',
    lead: 'What users see runs in the browser and calls the backend over HTTP.',
    blocks: [
      {
        heading: 'What it is',
        bullets: [
          'React — interactive pages without full reloads.',
          'Vite — fast dev server and bundler (rhymes with “feet”).',
          'TypeScript — JavaScript with types.',
          'Tailwind CSS — utility styling.',
          'React Router — different URLs, different pages.',
        ],
      },
      {
        heading: 'How it talks to the backend',
        bullets: [
          'Axios sends HTTP requests.',
          'Base URL from VITE_BACKEND_URL (default in code: http://localhost:8000).',
          'API paths: {VITE_BACKEND_URL}/api/v1/... (e.g. /api/v1/user, /api/v1/auth).',
        ],
      },
      {
        heading: 'How login is remembered',
        paragraphs: [
          'After login, a token is stored in the browser under localStorage key “dizeeToken”. Later requests send that token so the server knows who you are.',
        ],
      },
      {
        heading: 'Other bits',
        bullets: [
          'Optional GA4: VITE_GA4_ID at build time.',
          'Vite dev server default port: 3000.',
          'Production build output: build/',
        ],
      },
      {
        heading: 'Common commands (dizee folder)',
        code: ['npm install', 'npm run dev    → local dev server', 'npm run build  → production build'],
      },
    ],
  },
  {
    id: 'backend',
    number: 3,
    icon: Server,
    title: 'Backend (the server)',
    subtitle: 'Node, Express, TypeScript — routes and security at a glance',
    color: 'emerald',
    lead: 'A long-running Node process handles API requests; not a single “serverless” function for everything.',
    blocks: [
      {
        heading: 'What it is',
        bullets: [
          'Node.js — JavaScript on the server.',
          'Express — routes HTTP requests to handlers.',
          'TypeScript compiled to JS; production entry: build/server.js.',
          'Default port 8000 (or PORT from env).',
        ],
      },
      {
        heading: 'How it starts',
        bullets: [
          'Development: loads .env.dev',
          'Production: loads .env',
          'Connects to MongoDB via Mongoose.',
        ],
      },
      {
        heading: 'Main API layout (/api/v1/...)',
        table: {
          headers: ['Path', 'Purpose'],
          rows: [
            ['/auth', 'Signup, login, tokens'],
            ['/stripe', 'Payments (Stripe)'],
            ['/user', 'Most logged-in user features'],
            ['/vault', 'Vault'],
            ['/focus', 'Focus page'],
            ['/aws', 'Uploads (S3-backed)'],
            ['/setting', 'Settings'],
            ['/analytics', 'Analytics'],
            ['/analytics-tracking', 'Tracking'],
            ['/admin', 'Admin tools'],
            ['/feedback', 'Feedback'],
            ['/waitlist', 'Waitlist'],
            ['/team', 'Team invites (Mailgun), billing pieces'],
            ['/promoter', 'Promoter flows'],
            ['/experts', 'Experts'],
            ['/usage', 'Usage tracking'],
            ['/push', 'Web push (VAPID)'],
            ['/user/connectors', 'Connectors'],
            ['/integrations', 'Integrations (e.g. Google Sheets)'],
            ['/soundcharts', 'Soundcharts API'],
            ['/catalog', 'Catalog refresh'],
            ['/financials', 'Financials'],
            ['/activity', 'Activity logging'],
          ],
        },
      },
      {
        paragraphs: ['Also: /api/spotify-artists-agent — Spotify artist agent features.'],
      },
      {
        heading: 'Security (short)',
        bullets: [
          'Helmet, rate limiting, CORS.',
          'Production CORS: diz.ee domains + ALLOWED_ORIGINS.',
        ],
      },
      {
        heading: 'Common commands (backend-dizee folder)',
        code: ['npm install', 'npm run dev', 'npm run build && npm start'],
      },
    ],
  },
  {
    id: 'database',
    number: 4,
    icon: Database,
    title: 'Database (MongoDB Atlas)',
    subtitle: 'Documents, Mongoose, LOCAL_DB_URL',
    color: 'cyan',
    lead: 'Data lives in MongoDB Atlas; the app uses Mongoose to read and write.',
    blocks: [
      {
        heading: 'What MongoDB is',
        bullets: [
          'Document database — JSON-like documents in collections.',
          'Atlas — MongoDB’s cloud; you get a connection string.',
        ],
      },
      {
        heading: 'How we connect',
        bullets: [
          'Mongoose talks to MongoDB.',
          'Connection string: environment variable LOCAL_DB_URL (in practice your Atlas URI in deployed envs).',
          'If LOCAL_DB_URL is missing, the server may still start but DB calls fail.',
        ],
      },
      {
        paragraphs: [
          'If “the app is broken,” check: Atlas cluster up, network access allows the server IP, LOCAL_DB_URL correct.',
        ],
      },
    ],
  },
  {
    id: 'ai',
    number: 5,
    icon: Bot,
    title: 'AI (OpenAI)',
    subtitle: 'Models, API key, where they show up in the product',
    color: 'amber',
    lead:
      'OPENAI_API_KEY in the backend. Models change — verify in code or with engineering before quoting externally.',
    blocks: [
      {
        heading: 'Models referenced in code (can change)',
        bullets: [
          'gpt-4o — manager AI, OpenAI controller, Spotify artists agent, vision-style flows; sometimes gpt-4o-2024-08-06 for images.',
          'gpt-4 — some Gmail / transcription helpers.',
          'gpt-3.5-turbo — lighter paths (e.g. parts of upload route).',
          'whisper-1 — speech-to-text.',
          'text-embedding-3-small — knowledge-base embeddings (in-memory vector store today; may move to a vector DB).',
        ],
      },
      {
        paragraphs: [
          'For “what model?” questions: assistant-style features mostly use the GPT-4o family; always confirm in code.',
        ],
      },
    ],
  },
  {
    id: 'email',
    number: 6,
    icon: Mail,
    title: 'Email (Mailgun)',
    subtitle: 'Transactional mail — invites and notifications',
    color: 'rose',
    lead: 'We use Mailgun instead of running our own mail servers.',
    blocks: [
      {
        heading: 'Environment',
        bullets: ['MAILGUN_KEY', 'mailgun_domain (lowercase in team controller code)'],
      },
      {
        paragraphs: [
          'Team invitations and related flows send from your Mailgun domain (e.g. noreply@your-verified-domain).',
        ],
      },
      {
        paragraphs: ['If mail stops: Mailgun dashboard, DNS, env vars.'],
      },
    ],
  },
  {
    id: 'services',
    number: 7,
    icon: Layers,
    title: 'Other important services',
    subtitle: 'Stripe, Twilio, S3, push, Google, Spotify, and more',
    color: 'indigo',
    lead: 'Short map of third parties and what they’re for.',
    blocks: [
      {
        heading: 'Stripe',
        bullets: ['STRIPE_KEY, STRIPE_*_PRICE_ID — manager subscriptions; FRONTEND_URL for success/cancel.'],
      },
      {
        heading: 'Twilio',
        bullets: ['twilio_sid, twilio_token, twilio_phone_number — SMS / verification.'],
      },
      {
        heading: 'AWS S3',
        bullets: ['AWS_ACCESS_KEY, AWS_SECRET_KEY, AWS_BUCKET_NAME, AWS_REGION — uploads via /api/v1/aws.'],
      },
      {
        heading: 'Web push',
        bullets: ['VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY, VAPID_SUBJECT — browser notifications.'],
      },
      {
        heading: 'Google',
        bullets: ['Gmail, Meet, Sheets, etc. — OAuth + per-feature credentials.'],
      },
      {
        heading: 'Spotify / Soundcharts / SerpAPI / Puppeteer',
        bullets: [
          'SPOTIFY_CLIENT_ID, SPOTIFY_CLIENT_SECRET',
          'SOUNDCHARTS_APP_ID, SOUNDCHARTS_API_KEY, optional timeouts',
          'SERP_API_KEY',
          'PUPPETEER_EXECUTABLE_PATH (optional)',
        ],
      },
      {
        heading: 'Admin & URLs',
        bullets: [
          'ADMIN_EMAILS, ADMIN_PASSWORD, ACESSS_TOKEN_SECRET (typo preserved in env name).',
          'FRONTEND_URL — emails, Stripe redirects.',
          'BACKEND_URL / REACT_APP_BACKEND_URL — server self-reference, OAuth callbacks.',
        ],
      },
    ],
  },
  {
    id: 'elevator',
    number: 8,
    icon: MessageCircle,
    title: 'Explain it in one meeting',
    subtitle: 'Six bullets you can read aloud',
    color: 'zinc',
    lead: 'Use this when someone asks “how does Dizee work?” and you have two minutes.',
    blocks: [
      {
        numbered: [
          'Users use a React app built with Vite.',
          'That app calls our Express API on Node.',
          'Data lives in MongoDB Atlas; the server uses Mongoose.',
          'AI features call OpenAI (mostly GPT-4o class + Whisper + embeddings).',
          'Transactional email goes through Mailgun.',
          'Files → S3; payments → Stripe; SMS → Twilio; browser push → VAPID.',
        ],
      },
      {
        paragraphs: [
          '“Is it serverless?” — Backend is a long-running Node/Express process (e.g. node build/server.js), not one Lambda for the whole API.',
        ],
      },
    ],
  },
  {
    id: 'env',
    number: 9,
    icon: KeyRound,
    title: 'Environment variables cheat sheet',
    subtitle: 'Names only — never commit real secrets',
    color: 'sky',
    lead: 'Quick reference for frontend vs backend config.',
    blocks: [
      {
        heading: 'Frontend (Vite — must use VITE_ prefix for browser)',
        code: ['VITE_BACKEND_URL', 'VITE_GA4_ID', '(optional: Google Sheets template ID, etc.)'],
      },
      {
        heading: 'Backend (backend-dizee — .env / .env.dev)',
        table: {
          headers: ['Variable', 'Role'],
          rows: [
            ['LOCAL_DB_URL', 'MongoDB (Atlas) connection string'],
            ['OPENAI_API_KEY', 'OpenAI'],
            ['MAILGUN_KEY, mailgun_domain', 'Email'],
            ['FRONTEND_URL, BACKEND_URL', 'App URLs'],
            ['STRIPE_KEY, STRIPE_*_PRICE_ID', 'Billing'],
            ['twilio_*', 'SMS'],
            ['AWS_*', 'S3 uploads'],
            ['VAPID_*', 'Web push'],
            ['SPOTIFY_*', 'Spotify'],
            ['SOUNDCHARTS_*', 'Soundcharts'],
            ['SERP_API_KEY', 'Search API'],
            ['ALLOWED_ORIGINS', 'Production CORS'],
            ['PORT, NODE_ENV', 'Runtime'],
            ['ADMIN_*, ACESSS_TOKEN_SECRET', 'Admin / JWT'],
          ],
        },
      },
      {
        paragraphs: [
          'Plus Google/Gmail/Sheets-related vars per integration docs.',
        ],
      },
    ],
  },
  {
    id: 'disclaimer',
    number: 10,
    icon: Info,
    title: 'Disclaimer',
    subtitle: 'This doc is a snapshot — verify with engineering for production details',
    color: 'zinc',
    lead: 'Routes, env names, and models change as the product evolves.',
    blocks: [
      {
        paragraphs: [
          'For API details, use backend-dizee/docs when available, or ask engineering.',
        ],
      },
    ],
  },
]
